import { createMemo, For, Show, type Component } from "solid-js"

const ACTIONS = [
  "prepare_publish",
  "approve_publish",
  "apply_publish",
  "list_publish_checkpoints",
  "read_publish_checkpoint",
  "read_publish_receipt",
] as const

type PublishingAction = (typeof ACTIONS)[number]
type PublishingState = "pending" | "running" | "succeeded" | "error" | "retrying" | "resumed" | "not_found"

type PublishingNextAction = {
  action: PublishingAction
  label: string
  kind: "prompt"
  requiresConfirmation: true
  prompt: string
}

type PublishingContract = {
  schemaVersion: "1.0"
  product: "10xdotin-publishing"
  action: PublishingAction
  phase: string
  status: string
  summary: string
  identifiers: { handle?: string; checkpointId?: string; deploymentId?: string }
  links: { previewUrl?: string; liveUrl?: string }
  metrics: { fileCount?: number; totalBytes?: number }
  intent: { activate?: boolean }
  approval?: { approvedBy?: string; approvedAt?: string }
  upload?: { status?: string; fileCount?: number; totalBytes?: number; retryCount?: number }
  receiptState?: string
  nextActions: PublishingNextAction[]
  [key: string]: unknown
}

type PublishingToolInput = {
  tool: string
  status?: string
  input: Record<string, unknown>
  metadata: Record<string, unknown>
  output?: string
}

type PublishingToolProps = PublishingToolInput & {
  sessionID?: string
  hideDetails?: boolean
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  deferContent?: boolean
  virtualizeDiff?: boolean
  forceOpen?: boolean
  locked?: boolean
}

const SERVER = "tenxdotin-publishing-plugin"
const alias = (action: PublishingAction) => [
  `${SERVER}_${action}`,
  `${SERVER}:${action}`,
  `mcp__${SERVER}__${action}`,
  action,
]

export const TENX_PUBLISHING_TOOL_NAMES = ACTIONS.flatMap(alias)

function record(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return
  return value as Record<string, unknown>
}

function string(value: unknown) {
  return typeof value === "string" && value.trim() ? value : undefined
}

function number(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined
}

function boolean(value: unknown) {
  return typeof value === "boolean" ? value : undefined
}

function action(value: unknown): PublishingAction | undefined {
  return typeof value === "string" && ACTIONS.includes(value as PublishingAction)
    ? (value as PublishingAction)
    : undefined
}

function url(value: unknown) {
  const candidate = string(value)
  if (!candidate) return
  try {
    const parsed = new URL(candidate)
    if (parsed.protocol === "http:" || parsed.protocol === "https:") return parsed.href
  } catch {
    return
  }
}

function nextActions(value: unknown) {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    const row = record(item)
    const nextAction = action(row?.action)
    const label = string(row?.label)
    const prompt = string(row?.prompt)
    if (!row || !nextAction || !label || row.kind !== "prompt" || row.requiresConfirmation !== true || !prompt)
      return []
    return [{ action: nextAction, label, kind: "prompt" as const, requiresConfirmation: true as const, prompt }]
  })
}

function contract(value: unknown): PublishingContract | undefined {
  const row = record(value)
  const identifiers = record(row?.identifiers)
  const links = record(row?.links)
  const metrics = record(row?.metrics)
  const intent = record(row?.intent)
  const approval = record(row?.approval)
  const upload = record(row?.upload)
  const toolAction = action(row?.action)
  const phase = string(row?.phase)
  const status = string(row?.status)
  const summary = string(row?.summary)
  if (
    !row ||
    row.schemaVersion !== "1.0" ||
    row.product !== "10xdotin-publishing" ||
    !toolAction ||
    !phase ||
    !status ||
    !summary ||
    !identifiers ||
    !links ||
    !metrics ||
    !intent
  )
    return

  return {
    ...row,
    schemaVersion: "1.0",
    product: "10xdotin-publishing",
    action: toolAction,
    phase,
    status,
    summary,
    identifiers: {
      handle: string(identifiers.handle),
      checkpointId: string(identifiers.checkpointId),
      deploymentId: string(identifiers.deploymentId),
    },
    links: {
      previewUrl: url(links.previewUrl),
      liveUrl: url(links.liveUrl),
    },
    metrics: {
      fileCount: number(metrics.fileCount),
      totalBytes: number(metrics.totalBytes),
    },
    intent: { activate: boolean(intent.activate) },
    ...(approval
      ? {
          approval: { approvedBy: string(approval.approvedBy), approvedAt: string(approval.approvedAt) },
        }
      : {}),
    ...(upload
      ? {
          upload: {
            status: string(upload.status),
            fileCount: number(upload.fileCount),
            totalBytes: number(upload.totalBytes),
            retryCount: number(upload.retryCount),
          },
        }
      : {}),
    receiptState: string(row.receiptState),
    nextActions: nextActions(row.nextActions),
  }
}

export function publishingToolAction(tool: string) {
  return ACTIONS.find((candidate) => alias(candidate).includes(tool))
}

export function isTenxPublishingTool(tool: string) {
  return publishingToolAction(tool) !== undefined
}

export function parseTenxPublishingTool(input: PublishingToolInput) {
  const mcp = record(input.metadata.mcp)
  const structured = contract(mcp?.structuredContent)
  const state: PublishingState =
    input.status === "pending"
      ? "pending"
      : input.status === "running"
        ? "running"
        : input.status === "error"
          ? "error"
          : structured?.upload?.status === "retrying"
            ? "retrying"
            : structured?.upload?.status === "resumed"
              ? "resumed"
              : structured?.status === "not_found"
                ? "not_found"
                : "succeeded"
  return {
    action: publishingToolAction(input.tool),
    state,
    contract: structured,
    fallbackOutput: structured ? undefined : "Historical publishing result. Structured details are unavailable.",
    nextPrompt: structured?.nextActions[0]?.prompt,
  }
}

function actionLabel(value: PublishingAction | undefined) {
  if (value === "prepare_publish") return "Prepare"
  if (value === "approve_publish") return "Approve"
  if (value === "apply_publish") return "Apply"
  if (value === "list_publish_checkpoints") return "Publish history"
  if (value === "read_publish_checkpoint") return "Checkpoint"
  if (value === "read_publish_receipt") return "Deployment receipt"
  return "Publishing"
}

function stateLabel(value: PublishingState) {
  if (value === "pending") return "Pending"
  if (value === "running") return "Running"
  if (value === "retrying") return "Retrying upload"
  if (value === "resumed") return "Upload resumed"
  if (value === "error") return "Failed"
  if (value === "not_found") return "Not found"
  return "Succeeded"
}

function bytes(value: number | undefined) {
  if (value === undefined) return
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

async function copy(value: string) {
  if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return
  await navigator.clipboard.writeText(value)
}

export function TenxPublishingTool(props: PublishingToolProps) {
  const parsed = createMemo(() => parseTenxPublishingTool(props))
  const data = createMemo(() => parsed().contract)
  const identifiers = createMemo(() => data()?.identifiers ?? {})
  const sourcePath = createMemo(() => string(record(data()?.preview)?.sourcePath) ?? string(props.input.sourcePath))
  const fields = createMemo(() =>
    [
      ["Handle", identifiers().handle],
      ["Checkpoint", identifiers().checkpointId],
      ["Deployment", identifiers().deploymentId],
      ["Source", sourcePath()],
      ["Files", data()?.metrics.fileCount?.toString()],
      ["Bytes", bytes(data()?.metrics.totalBytes)],
      ["Activate", data()?.intent.activate === undefined ? undefined : data()?.intent.activate ? "Yes" : "No"],
      ["Approved by", data()?.approval?.approvedBy],
      ["Approved at", data()?.approval?.approvedAt],
      ["Upload", data()?.upload?.status],
      ["Retries", data()?.upload?.retryCount?.toString()],
      ["Receipt", data()?.receiptState],
    ].filter((item): item is [string, string] => typeof item[1] === "string" && !!item[1]),
  )
  const summary = createMemo(() => {
    if (data()) return data()!.summary
    if (parsed().state === "pending") return `${actionLabel(parsed().action)} is queued.`
    if (parsed().state === "running") return `${actionLabel(parsed().action)} is in progress.`
    if (parsed().state === "error") return `${actionLabel(parsed().action)} did not complete.`
    return parsed().fallbackOutput ?? "Historical publishing result."
  })

  return (
    <article
      data-component="tenx-publishing-tool"
      data-phase={data()?.phase ?? parsed().action ?? "unknown"}
      data-state={parsed().state}
    >
      <header data-slot="tenx-publishing-header">
        <div data-slot="tenx-publishing-heading">
          <span data-slot="tenx-publishing-product">10x Publishing</span>
          <span data-slot="tenx-publishing-action">{actionLabel(parsed().action)}</span>
        </div>
        <span data-slot="tenx-publishing-status">{stateLabel(parsed().state)}</span>
      </header>
      <p data-slot="tenx-publishing-summary">{summary()}</p>
      <Show when={fields().length}>
        <dl data-slot="tenx-publishing-fields">
          <For each={fields()}>
            {([label, value]) => (
              <div data-slot="tenx-publishing-field">
                <dt>{label}</dt>
                <dd title={value}>{value}</dd>
              </div>
            )}
          </For>
        </dl>
      </Show>
      <div data-slot="tenx-publishing-actions">
        <Show when={data()?.links.previewUrl}>
          {(href) => (
            <a href={href()} target="_blank" rel="noopener noreferrer">
              Open preview
            </a>
          )}
        </Show>
        <Show when={data()?.links.liveUrl}>
          {(href) => (
            <a href={href()} target="_blank" rel="noopener noreferrer">
              Open live site
            </a>
          )}
        </Show>
        <Show when={identifiers().checkpointId}>
          {(value) => (
            <button type="button" onClick={() => copy(value())} aria-label="Copy checkpoint ID">
              Copy checkpoint ID
            </button>
          )}
        </Show>
        <Show when={identifiers().deploymentId}>
          {(value) => (
            <button type="button" onClick={() => copy(value())} aria-label="Copy deployment ID">
              Copy deployment ID
            </button>
          )}
        </Show>
        <Show when={sourcePath()}>
          {(value) => (
            <button type="button" onClick={() => copy(value())} aria-label="Copy source path">
              Copy source path
            </button>
          )}
        </Show>
        <Show when={parsed().nextPrompt}>
          {(prompt) => (
            <button type="button" onClick={() => copy(prompt())} aria-label="Copy permission-gated next-step prompt">
              Copy next-step prompt
            </button>
          )}
        </Show>
      </div>
      <Show when={!props.hideDetails && (data() || parsed().fallbackOutput)}>
        <details data-slot="tenx-publishing-details" open={props.defaultOpen}>
          <summary>Review details</summary>
          <pre>{data() ? JSON.stringify(data(), null, 2) : parsed().fallbackOutput}</pre>
        </details>
      </Show>
    </article>
  )
}

export function registerTenxPublishingTools(registry: {
  register: (input: { name: string; render: Component<PublishingToolProps> }) => unknown
}) {
  TENX_PUBLISHING_TOOL_NAMES.forEach((name) => registry.register({ name, render: TenxPublishingTool }))
}
